import React, {
  useEffect,
  useMemo,
  useState,
  useId,
  createContext,
  useContext,
  useRef,
} from "react";
import { Table, Input, Button, DatePicker, Select, Space, message, Modal } from "antd";
import {
  DownloadOutlined,
  MenuOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import classes from "./TableWithExport.module.scss";
import {
  getName,
  getEmail,
  getPhone,
  getCreated,
  exportCsv,
} from "./TableWithExport.utils";

const { RangePicker } = DatePicker;

const DragHandleCtx = createContext(null);
const DragHandle = () => {
  const handleProps = useContext(DragHandleCtx) || {};
  return (
    <span
      {...handleProps}
      style={{ cursor: "grab", display: "inline-flex", alignItems: "center" }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <MenuOutlined />
    </span>
  );
};

export const TableWithExport = ({
  columns,
  data = [],
  loading,
  setCurrent,
  setOpen,
  tableName,
  filters,
  enableReorder = false,
  onReorder,
  getRowKey: getRowKeyProp,
  addRow = false,
  disableExport = false,
  selectable = false,
  onBulkDelete,
}) => {
  const [q, setQ] = useState("");
  const [range, setRange] = useState(null);
  const [adminFilter, setAdminFilter] = useState("all");
  const [verifiedFilter, setVerifiedFilter] = useState("all");

  const textFiltered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return data;
    return data.filter((u) =>
      [getName(u), getEmail(u), u.location, getPhone(u)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [q, data]);

  const dateFiltered = useMemo(() => {
    const [from, to] = range || [];
    if (!from && !to) return textFiltered;
    const start = from ? from.startOf("day").valueOf() : -Infinity;
    const end = to ? to.endOf("day").valueOf() : Infinity;
    return textFiltered.filter((u) => {
      const d = getCreated(u);
      if (!d) return false;
      const t = new Date(d).getTime();
      return t >= start && t <= end;
    });
  }, [textFiltered, range]);

  const finalFiltered = useMemo(() => {
    return (dateFiltered || []).filter((u) => {
      const isAdmin = !!u?.isAdmin;
      const isVerified = !!u?.verified;
      const adminOk =
        adminFilter === "all" || (adminFilter === "admin" ? isAdmin : !isAdmin);
      const verifiedOk =
        verifiedFilter === "all" ||
        (verifiedFilter === "verified" ? isVerified : !isVerified);
      return adminOk && verifiedOk;
    });
  }, [dateFiltered, adminFilter, verifiedFilter]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => setPage(1), [q, range, adminFilter, verifiedFilter]);

  const getRowKey =
    getRowKeyProp ||
    ((u) =>
      String(u?._id || u?.id || getEmail(u) || Math.random().toString(36)));

  const [reorderMode, setReorderMode] = useState(false);
  const [dndRows, setDndRows] = useState([]);
  const [savingReorder, setSavingReorder] = useState(false);
  const snapshotRef = useRef(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  useEffect(() => {
    // Clear selection when data changes
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [data]);

  useEffect(() => {
    if (reorderMode) setDndRows(finalFiltered);
  }, [reorderMode, finalFiltered]);

  const droppableId = useId() || "table-dnd";

  const onBeforeDragStart = () => {
    snapshotRef.current = dndRows;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    const next = Array.from(dndRows);
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    setDndRows(next);
  };

  const saveNewOrder = async () => {
    if (!onReorder) {
      message.warning("onReorder callback not provided");
      return;
    }
    try {
      setSavingReorder(true);
      const ids = dndRows.map((r) => getRowKey(r));
      await Promise.resolve(onReorder(ids, dndRows));
      message.success("Order saved");
      setReorderMode(false);
    } catch (e) {
      message.error(e?.message || "Failed to save order");
      if (snapshotRef.current) setDndRows(snapshotRef.current);
    } finally {
      setSavingReorder(false);
    }
  };

  const columnsWithReorder = useMemo(() => {
    if (!reorderMode) return columns;
    return [
      {
        title: "",
        dataIndex: "__drag",
        width: 44,
        render: () => <DragHandle />,
      },
      ...columns,
    ];
  }, [columns, reorderMode]);

  const DraggableContainer = React.forwardRef(function DraggableContainer(
    props,
    ref
  ) {
    const { children, ...rest } = props;
    return (
      <Droppable
        droppableId={droppableId}
        direction="vertical"
        renderClone={(provided, snapshot, rubric) => {
          const item = dndRows[rubric.source.index];

          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={{
                ...provided.draggableProps.style,
                boxShadow: "0 4px 12px rgba(0,0,0,.15)",
                background: "#fff",
                padding: 12,
                borderRadius: 6,
                willChange: "transform",
              }}
            >
              {/* Show a recognizable label; tweak as you like */}
              {String(item?.title || item?.name || getEmail(item) || "Row")}
            </div>
          );
        }}
      >
        {(provided) => (
          <tbody
            ref={(node) => {
              provided.innerRef(node);
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            }}
            {...rest}
            {...provided.droppableProps}
          >
            {children}
            {provided.placeholder}
          </tbody>
        )}
      </Droppable>
    );
  });

  const DraggableBodyRow = (props) => {
    const rowKey = props["data-row-key"];
    const index = dndRows.findIndex(
      (r) => String(getRowKey(r)) === String(rowKey)
    );
    if (index === -1) return <tr {...props} />;

    return (
      <Draggable
        draggableId={String(rowKey)}
        index={index}
        isDragDisabled={!reorderMode}
      >
        {(provided, snapshot) => (
          <DragHandleCtx.Provider value={provided.dragHandleProps}>
            <tr
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...props}
              style={{
                ...props.style,
                ...provided.draggableProps.style,
                cursor: reorderMode ? "grab" : undefined,
                userSelect: reorderMode ? "none" : undefined,
                willChange: "transform",
                ...(snapshot.isDragging ? { background: "#fafafa" } : null),
              }}
            />
          </DragHandleCtx.Provider>
        )}
      </Draggable>
    );
  };

  const tableComponents = reorderMode
    ? { body: { wrapper: DraggableContainer, row: DraggableBodyRow } }
    : undefined;

  const DndWrapper = ({ children }) =>
    reorderMode ? (
      <DragDropContext
        onBeforeDragStart={onBeforeDragStart}
        onDragEnd={onDragEnd}
      >
        {children}
      </DragDropContext>
    ) : (
      children
    );

  const dataSource = reorderMode ? dndRows : finalFiltered;

  return (
    <div
      className={`${classes.wrapper} ${reorderMode ? classes.reorderMode : ""}`}
    >
      <div className={classes.wrapper__head}>
        <div className={classes.wrapper__head__search}>
          <Input.Search
            placeholder="Search..."
            allowClear
            style={{ maxWidth: 260 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onSearch={(v) => setQ(v)}
            disabled={reorderMode}
          />
          {filters && (
            <>
              <Select
                value={adminFilter}
                onChange={setAdminFilter}
                style={{ width: 160 }}
                disabled={reorderMode}
                options={[
                  { value: "all", label: "All Users" },
                  { value: "admin", label: "Admins" },
                  { value: "non-admin", label: "Non-admins" },
                ]}
              />
              <Select
                value={verifiedFilter}
                onChange={setVerifiedFilter}
                style={{ width: 200 }}
                disabled={reorderMode}
                options={[
                  { value: "all", label: "All (Verified, Unverified)" },
                  { value: "verified", label: "Verified only" },
                  { value: "unverified", label: "Unverified only" },
                ]}
              />
            </>
          )}
        </div>

        <div className={classes.wrapper__head__export}>
          <Space wrap>
            <RangePicker
              value={range}
              onChange={(vals) => setRange(vals)}
              allowClear
              disabled={reorderMode}
            />
            {addRow && !reorderMode && (
              <>
                <Button
                  type="primary"
                  style={{ textTransform: "capitalize" }}
                  icon={<PlusOutlined />}
                  onClick={addRow}
                >
                  Add {tableName.replaceAll("_", " ")}
                </Button>
              </>
            )}
            {selectable && !reorderMode && (
              <Button
                danger
                disabled={!selectedRows.length}
                onClick={() => onBulkDelete && onBulkDelete(selectedRows)}
              >
                Delete{selectedRows.length ? ` (${selectedRows.length})` : ""}
              </Button>
            )}
            {enableReorder && reorderMode && (
              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={savingReorder}
                  onClick={saveNewOrder}
                >
                  Save order
                </Button>
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setReorderMode(false);
                    setDndRows([]);
                  }}
                >
                  Cancel
                </Button>
              </Space>
            )}

            {!disableExport && (
              <Button
                icon={<DownloadOutlined />}
                type="primary"
                disabled={reorderMode}
                onClick={() =>
                  exportCsv(
                    finalFiltered,
                    range,
                    adminFilter,
                    verifiedFilter,
                    tableName
                  )
                }
              >
                Export CSV
              </Button>
            )}

            {enableReorder && !reorderMode && (
              <Button
                icon={<MenuOutlined />}
                onClick={() => {
                  if (!finalFiltered.length) {
                    message.info("No rows to reorder");
                    return;
                  }
                  setReorderMode(true);
                }}
              >
                Reorder
              </Button>
            )}
          </Space>
        </div>
      </div>

      <DndWrapper>
        <Table
          rowKey={(u) => getRowKey(u)}
          rowSelection={
            selectable && !reorderMode
              ? {
                  selectedRowKeys,
                  onChange: (keys, rows) => {
                    setSelectedRowKeys(keys);
                    setSelectedRows(rows);
                  },
                }
              : undefined
          }
          columns={columnsWithReorder}
          dataSource={dataSource}
          loading={loading}
          components={tableComponents}
          tableLayout={reorderMode ? "fixed" : undefined}
          pagination={
            reorderMode
              ? false
              : {
                  current: page,
                  pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: [5, 10, 20, 50, 100],
                  onChange: (p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                  },
                }
          }
          scroll={{ x: true }}
          onRow={(record) => ({
            onDoubleClick: () => {
              if (reorderMode) return;
              setCurrent && setCurrent(record);
              setOpen && setOpen(true);
            },
          })}
        />
      </DndWrapper>
    </div>
  );
};

export default TableWithExport;
